import React, { useEffect, useRef, useState } from 'react';
import { useFeatures } from '../utils/features';
import { currentActor } from '../utils/beacons';
import { PalletRow, usePalletRows } from '../utils/palletBeaconsPrompt';
import { isDownstream, loadTaskPallets, taskPalletLights } from '../features/queue/palletBeacons';
import HoldingMoveDialog from './HoldingMoveDialog';

// The job's pallets at a station after the laser (Brake, Welding, Fabrication, ...), 2026-10-26 (Mike, after the
// beacon lifecycle review: a downstream operator opening a task had no way to find the pallet of parts it works
// on). Opening a task reads GET /tasks/{id}/pallet_beacons: the pallets holding this task's parts NOW (a cut list's
// parts for a part-less saw / lathe task), each with its beacon, where it was last placed (one line + Show map) and
// its own Find. "Find all" lights every one; "Lights off" stops this task's own lights. A Find is left alone over
// another instruction on the screen or while the laser still fills the pallet (the row says why). Another task's
// light reads "flashing for another task". Nothing lights on its own. The
// API never lets a station after the laser put a beacon on or take one off (no Swap / Put a beacon on it here),
// and its light never takes the pallet from the laser task that filled it.
// A holding pallet's row (the API's `holding`) offers Move pieces (2026-10-29, Mike, SO-1145): combine it with another
// of the order's holding pallets or split it onto a new one, the way the parts fit after this station.

const styles = {
  box: {
    border: '1px solid var(--jq-border-medium, #ddd)', borderRadius: 6, padding: '0.5rem 0.75rem',
    background: 'var(--jq-bg-card, #fff)', marginTop: '0.75rem',
  },
  head: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  title: { fontWeight: 600, marginRight: 'auto' },
  muted: { fontSize: '0.85rem', color: 'var(--bs-secondary-color, #6c757d)' },
};

export const NO_PALLETS_HERE = 'No pallet in the shop holds these parts right now (or pallet beacons are switched off).';

/** Shown for a task at a station after the laser (the laser has its Start popup and "Pallets for this task"). */
export function showTaskPalletsPanel(workstation) {
  return isDownstream(workstation);
}

export default function TaskPalletsPanel({ taskId, workstation, className = '' }) {
  const enabled = useFeatures().palletBeacons.enabled;
  const show = enabled && !!taskId && showTaskPalletsPanel(workstation);
  const [state, setState] = useState({ loading: false, error: '', loadedFor: null, pallets: [], station: '' });
  const seq = useRef(0);

  useEffect(() => {
    if (!show) return undefined;
    const mine = ++seq.current;
    setState({ loading: true, error: '', loadedFor: null, pallets: [] });
    loadTaskPallets(taskId).then((r) => {
      if (mine !== seq.current) return;           // another task was opened meanwhile
      if (!r.ok || !r.body) {
        setState({ loading: false, error: `Couldn't read the pallets: ${r.message || 'try again'}`, loadedFor: taskId, pallets: [] });
        return;
      }
      setState({ loading: false, error: '', loadedFor: taskId,
        pallets: Array.isArray(r.body.pallets) ? r.body.pallets : [],
        // what the floor calls the station (the sub-process: Welding, Lathe ...), for the rows' words
        station: (typeof r.body.station === 'string' && r.body.station.trim()) || workstation });
    });
    return () => { seq.current += 1; };
  }, [show, taskId, workstation]);

  if (!show) return null;
  return (
    <div className={className} style={styles.box} data-testid="task-pallets-panel" aria-label="Pallets for this task">
      {state.loading && <div style={styles.muted} role="status">Reading the pallets…</div>}
      {!state.loading && state.error && <div className="small text-danger" role="alert">{state.error}</div>}
      {!state.loading && !state.error && state.loadedFor === taskId && (
        // keyed by task: a new task starts the rows over
        <PalletList key={taskId} taskId={taskId} station={state.station} initial={state.pallets} />
      )}
    </div>
  );
}

function PalletList({ taskId, station, initial }) {
  const { pallets, setPallets, busy, setBusy, error, setError, rowErrors, rowNotes, again, findOne } =
    usePalletRows(taskId, initial);
  const [moving, setMoving] = useState(null);     // the holding pallet whose Move pieces dialog is open
  const findable = pallets.filter((p) => p && p.beacon_id && !p.beacon_away);

  async function reload() {
    const r = await loadTaskPallets(taskId);
    if (r.ok && r.body && Array.isArray(r.body.pallets)) setPallets(r.body.pallets);
  }
  // Lights off stops only this task's own lights (the API's rule): offer it only when there is one
  const anyMine = pallets.some((p) => p && p.light && p.light_mine);

  async function lightsOff() {
    setBusy('off');
    setError('');
    const r = await taskPalletLights(taskId, 'off', { actor: currentActor() });
    setBusy('');
    if (r.ok && r.body && Array.isArray(r.body.pallets)) setPallets(r.body.pallets);
    else if (!r.ok) setError(r.message || "Couldn't turn the lights off.");
  }

  if (!pallets.length) {
    return <div style={styles.muted}>{NO_PALLETS_HERE}</div>;
  }
  return (
    <>
      <div style={styles.head}>
        <span style={styles.title}>Pallets with these parts ({pallets.length})</span>
        {findable.length > 0 && (
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={again} disabled={!!busy}
            data-testid="task-pallets-find-all">
            {busy === 'again' ? 'Lighting…' : `Find all (${findable.length})`}
          </button>
        )}
        {anyMine && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={lightsOff} disabled={!!busy}>
            {busy === 'off' ? 'Turning off…' : 'Lights off'}
          </button>
        )}
      </div>
      {pallets.map((p) => (
        <PalletRow key={p.pallet_id} pallet={p} busy={busy} station={station} error={rowErrors[p.pallet_id]}
          note="" refusalNote={rowNotes[p.pallet_id] || ''} onSwap={() => {}} onAssign={() => {}}
          onFind={() => findOne(p)} onMove={() => setMoving(p.pallet_id)} />
      ))}
      {error && <div className="small text-danger mt-1" role="status">{error}</div>}
      {moving != null && (
        <HoldingMoveDialog show palletId={moving} taskId={taskId} onClose={() => setMoving(null)} onMoved={reload} />
      )}
    </>
  );
}
